/**
 * Вкладка «Производство» карточки товара в каталоге пряников.
 *
 * Показывает то, чего нет в обычной карточке: превью листа, габариты и
 * площади, вес, долю листа, состав набора или «входит в наборы», файлы
 * производства и ссылку на задание генератора.
 *
 * Файлы печати здесь не из `PrintFilesTab` карточки макета: тот читает
 * список конвейера PHOTOGEN по коду макета, а файлы пряников (лист JPG,
 * PLT, форма STL) лежат в хранилище как артефакты товара и скачиваются теми
 * же вызовами, что и на странице задания генератора.
 */

import { useState } from 'react'
import { useMoldReadiness } from '../../gingerbread-release/useMoldReadiness'
import { MoldReadinessBadge } from '../../gingerbread-release/MoldReadinessBadge'
import { MoldProductionDialog } from '../../gingerbread-release/MoldProductionDialog'
import { useAuthStore } from '../../../stores/authStore'
import { Link } from 'react-router-dom'
import { Download, ExternalLink, ImageOff, Loader2 } from 'lucide-react'
import { Badge } from '../../../components/ui/Badge'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useToastStore } from '../../../stores/toastStore'
import { gingerbreadStudioAPI } from '../../gingerbread-studio/api'
import {
  COMPUTED_WEIGHT_HINT,
  formatArea,
  formatGrams,
  formatShape,
  formatSheetShare,
  formatSize,
  isComputedWeight,
  isPp2Kit,
  jobHref,
  KIND_LABEL,
} from '../lib'
import type { GingerbreadBlock, GingerbreadRow } from '../types'
import { useGingerbreadBlock } from '../useGingerbreadBlock'
import { resolvePreview } from './GingerbreadThumb'
import { MarketplaceVisualsSection } from './MarketplaceVisualsSection'
import { FileBadges } from './RowCells'

const ComputedMark = () => (
  <span
    title={COMPUTED_WEIGHT_HINT}
    className="ml-1.5 rounded bg-gray-100 px-1 py-0.5 text-2xs font-medium text-gray-500 dark:bg-zinc-800 dark:text-zinc-400"
  >
    расчётный
  </span>
)

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-gray-100 py-1.5 last:border-b-0 dark:border-zinc-800">
    <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-400">{label}</span>
    <span className="min-w-0 text-right text-sm text-gray-900 dark:text-zinc-100">{children}</span>
  </div>
)

const sectionTitle = 'mb-2 text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400'

interface DownloadSpec {
  key: string
  label: string
  fileName: string
  run: () => Promise<void>
}

/** Скачиваемые файлы: набор берёт файлы листа, пряник — печать и форму. */
function downloadSpecs(article: string, block: GingerbreadBlock): DownloadSpec[] {
  if (block.kind === 'kit' && block.kitId) {
    const kitId = block.kitId
    const spec = (key: string, label: string, kind: 'sheet_jpg' | 'sheet_plt' | 'kit_stl', suffix: string): DownloadSpec => ({
      key,
      label,
      fileName: `${article}${suffix}`,
      run: () => gingerbreadStudioAPI.downloadKitFile(kitId, kind, `${article}${suffix}`),
    })
    return [
      ...(block.files.sheetJpg ? [spec('jpg', 'Лист для печати (JPG)', 'sheet_jpg', '.jpg')] : []),
      ...(block.files.plt ? [spec('plt', 'Файл плоттера (PLT)', 'sheet_plt', '.plt')] : []),
      ...(block.files.stl ? [spec('stl', 'Форма под тесто (STL)', 'kit_stl', '.stl')] : []),
    ]
  }
  if (block.kind === 'single' && block.gingerbreadId) {
    const id = block.gingerbreadId
    return [
      ...(block.files.sheetJpg
        ? [{
            key: 'png',
            label: 'Печать (PNG)',
            fileName: `${article}.png`,
            run: () => gingerbreadStudioAPI.downloadFile(id, 'print_png', `${article}.png`),
          }]
        : []),
      ...(block.files.stl
        ? [{
            key: 'stl',
            label: 'Форма под тесто (STL)',
            fileName: `${article}.stl`,
            run: () => gingerbreadStudioAPI.downloadFile(id, 'cutter_stl', `${article}.stl`),
          }]
        : []),
    ]
  }
  return []
}

export function ProductionTab({ product }: { product: GingerbreadRow }) {
  const showToast = useToastStore((state) => state.showToast)
  // Блока нет, когда окно открыли по ссылке `?article=` или из другого окна, а
  // строки в списке нет: хук догружает ту же строку каталога.
  const { block, loading } = useGingerbreadBlock(product)
  const [busy, setBusy] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)
  const [moldOpen, setMoldOpen] = useState(false)
  const [moldReload, setMoldReload] = useState(0)
  const user = useAuthStore((state) => state.user)
  const canOrder = user?.role === 'admin' || user?.role === 'owner' || user?.permissions?.some((permission) => ['MoldProduction:CREATE', 'MoldProduction:MANAGE'].includes(permission))
  const moldState = useMoldReadiness(block?.kind === 'kit' && !isPp2Kit(block) ? [product.article] : [], moldReload)

  if (loading || block === undefined) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" aria-label="Загрузка" />
      </div>
    )
  }
  if (!block) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        Для этого товара нет производственных данных генератора пряников.
      </p>
    )
  }

  const row: GingerbreadRow = { ...product, gingerbread: block }
  const preview = resolvePreview(row)
  const downloads = downloadSpecs(product.article, block)
  const isKit = block.kind === 'kit'
  const isPp2 = isPp2Kit(block)
  const computedWeight = isComputedWeight(block)
  const sourceDesignCode = isPp2 && product.design?.design_code != null ? String(product.design.design_code) : null

  const download = async (spec: DownloadSpec) => {
    setBusy(spec.key)
    try {
      await spec.run()
    } catch {
      showToast('Не удалось скачать файл', 'error')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="gingerbread-production-tab">
      <div className="space-y-4">
        <div className="flex aspect-square items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-800">
          {preview && !previewFailed ? (
            <AuthorizedMediaImage
              src={preview.url}
              alt={`${preview.label} ${product.article}`}
              lazy={false}
              onError={() => setPreviewFailed(true)}
              className="h-full w-full object-contain"
              errorFallback={<ImageOff aria-label="Превью недоступно" className="h-10 w-10 text-gray-300 dark:text-zinc-600" />}
            />
          ) : (
            <ImageOff aria-label="Нет изображения" className="h-10 w-10 text-gray-300 dark:text-zinc-600" />
          )}
        </div>

        <section>
          <h4 className={sectionTitle}>Файлы производства</h4>
          {isKit && !isPp2 && <div className="mb-3 flex flex-wrap items-center gap-2">
            <MoldReadinessBadge item={moldState.items[product.article]} error={moldState.error} />
            {canOrder && <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setMoldOpen(true)}>Заказать формы</button>}
          </div>}
          {moldOpen && <MoldProductionDialog articles={[product.article]} onClose={() => setMoldOpen(false)} onChanged={() => setMoldReload((value) => value + 1)} />}
          <div className="mb-2">
            <FileBadges block={block} />
          </div>
          {downloads.length > 0 ? (
            <ul className="space-y-1.5">
              {downloads.map((spec) => (
                <li key={spec.key} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-gray-700 dark:text-zinc-200">{spec.label}</span>
                  <button
                    type="button"
                    disabled={busy === spec.key}
                    onClick={() => void download(spec)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {busy === spec.key ? 'Скачивание…' : 'Скачать'}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-500 dark:text-zinc-400">Готовых файлов для скачивания нет.</p>
          )}
        </section>
      </div>

      <div className="space-y-4">
        <section>
          <h4 className={sectionTitle}>Метрики</h4>
          <Row label="Вид">
            <Badge variant={isKit ? 'purple' : 'blue'}>{KIND_LABEL[block.kind]}</Badge>
          </Row>
          {!isKit && <Row label="Размер">{formatSize(block)}</Row>}
          {(!isKit || isPp2) && <Row label="Форма">{formatShape(block)}</Row>}
          {isPp2 && sourceDesignCode && (
            <Row label="Исходный макет «Капк 5»">
              <span className="font-mono text-[0.8125rem]">{sourceDesignCode}</span>
            </Row>
          )}
          {!isKit && <Row label="Площадь сахарной бумаги">{formatArea(block.sugarAreaCm2)}</Row>}
          {!isKit && <Row label="Площадь теста">{formatArea(block.doughAreaCm2)}</Row>}
          <Row label="Вес нетто">
            {formatGrams(block.weightNetG)}
            {computedWeight && <ComputedMark />}
          </Row>
          <Row label="Вес брутто">
            {formatGrams(block.weightGrossG)}
            {computedWeight && <ComputedMark />}
          </Row>
          {isKit && <Row label="Доля листа на позицию">{formatSheetShare(block)}</Row>}
          {block.depicted && <Row label="Что изображено">{block.depicted}</Row>}
          {block.textOnImage && <Row label="Текст на изображении">{block.textOnImage}</Row>}
        </section>

        {block.jobId && !isPp2 && (
          <Link
            to={jobHref(block.jobId)}
            className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline dark:text-indigo-400"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Открыть задание генератора
            {block.jobName && <span className="text-gray-500 dark:text-zinc-400">· {block.jobName}</span>}
          </Link>
        )}
      </div>

      {/* Визуалы PP2 делает конвейер макета «Капк 5», у генератора пряников их нет (эндпоинт отвечал 404). */}
      {!isPp2 && <MarketplaceVisualsSection article={product.article} kit={isKit} />}
    </div>
  )
}
